/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { SmallPropertyCard } from '../SmallPropertyCard/SmallPropertyCard';
import { useSearchFormStore } from '../../store/searchFormStore';
import { USE_POINTS } from '../../store/store.constants';

export interface PropertyListItem {
  node: {
    distance: number;
    property: {
      id: string;
      media: {
        primaryImage: {
          edges: Array<{
            node: {
              imageUrls: {
                wideHorizontal: string;
              };
            };
          }>;
        };
      };
      basicInformation: {
        nameInDefaultLanguage: string;
        location: string;
        rating: number;
        priceDisplay: string;
      };
      reviews: {
        numberOfReviews: {
          count: number;
        };
        stars?: {
          count: number;
        };
      };
    };
  };
}

export interface PropertyListProps {
  viewData: any; // ViewData from store
  onItemClick?: (propertyId: string) => void;
  recommendedPropertyIds?: string[]; // AI-recommended property IDs in order of relevance
}

export const PropertyList: React.FC<PropertyListProps> = ({ viewData, recommendedPropertyIds = [] }) => {
  // Check if user is searching with points/rewards redemption
  const usePointsState = useSearchFormStore((s: any) => s[USE_POINTS]);
  const isPointsSearch = usePointsState?.rewardsRedemption ?? false;

  // Extract properties from viewData
  const rawProperties = viewData.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges;

  if (!rawProperties || rawProperties.length === 0) return null;

  // Create a Set for O(1) lookup of recommended property IDs
  const recommendedSet = new Set(recommendedPropertyIds);

  // Reorder properties: recommended properties first (in order), then the rest
  const properties = [...rawProperties].sort((a: PropertyListItem, b: PropertyListItem) => {
    const aId = a.node?.property?.id;
    const bId = b.node?.property?.id;
    const aIsRecommended = recommendedSet.has(aId);
    const bIsRecommended = recommendedSet.has(bId);

    // If both are recommended, maintain their order from recommendedPropertyIds
    if (aIsRecommended && bIsRecommended) {
      return recommendedPropertyIds.indexOf(aId) - recommendedPropertyIds.indexOf(bId);
    }
    // Recommended properties come first
    if (aIsRecommended) return -1;
    if (bIsRecommended) return 1;
    // Non-recommended properties maintain original order
    return 0;
  });

  return (
    <div
      style={
        {
          // wrapper needed to host the style tag without polluting parent
        }
      }
    >
      <style
        // Hide scrollbar for WebKit-based browsers, keep scrolling functionality
        dangerouslySetInnerHTML={{
          __html:
            '.mi-scroll-x-hidden{ -ms-overflow-style: none; scrollbar-width: none; } .mi-scroll-x-hidden::-webkit-scrollbar{ display: none; }',
        }}
      />
      <div
        className="mi-scroll-x-hidden"
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '12px',
          overflowX: 'auto',
          paddingBottom: '8px',
          // Firefox + old Edge/IE
          scrollbarWidth: 'none' as unknown as undefined,
          msOverflowStyle: 'none',
        }}
      >
        {properties.map((property: PropertyListItem) => {
          const propertyId = property?.node?.property?.id;
          const isBestMatch = recommendedSet.has(propertyId);
          const ratingValue = property?.node?.property?.reviews?.stars?.count || 0;
          const reviewCountValue = property?.node?.property?.reviews?.numberOfReviews?.count || 0;
          return (
            <div key={propertyId} style={{ flex: '0 0 auto' }}>
              <SmallPropertyCard
                imageUrl={property?.node?.property?.media?.primaryImage?.edges?.[0]?.node.imageUrls.wideHorizontal}
                name={property?.node?.property?.basicInformation?.nameInDefaultLanguage}
                location={property?.node?.distance}
                ratingInfo={{
                  isMax: false,
                  reviewUrl: '',
                  reviewTitle: '',
                  ratingValue,
                  hasRating: ratingValue > 0,
                  reviewCountValue,
                }}
                priceDisplay={property?.node?.property?.basicInformation?.priceDisplay}
                edge={property as unknown as Record<string, unknown>}
                property={property?.node.property}
                propertyId={propertyId}
                isBestMatch={isBestMatch}
                priceDisplayMode={isPointsSearch ? 'points' : 'cash'}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
